import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPrice } from '../../data/products';
import './ProductCard.css';

const APPAREL_CATEGORIES = ['tshirts', 'pants'];

const TAG_LABELS = { new: 'new', best: 'best' };

const COLOR_SWATCHES = {
  black: { label: 'Black', value: '#171717' },
  white: { label: 'White', value: '#ffffff' },
  red: { label: 'Red', value: '#dc2626' },
  blue: { label: 'Blue', value: '#2563eb' },
  navy: { label: 'Navy', value: '#1e3a8a' },
  gray: { label: 'Gray', value: '#8b909a' },
  green: { label: 'Green', value: '#16a34a' },
  yellow: { label: 'Yellow', value: '#facc15' },
  brown: { label: 'Brown', value: '#8b5e3c' },
  beige: { label: 'Beige', value: '#d6c2a1' },
  pink: { label: 'Pink', value: '#ec4899' },
  olive: { label: 'Olive', value: '#5a5f2e' },
  charcoal: { label: 'Charcoal', value: '#36393f' },
  bone: { label: 'Bone', value: '#e8e2d4' },
  sand: { label: 'Sand', value: '#cbb894' },
};

export const colorSwatch = (color) => COLOR_SWATCHES[color] || (/^#[\da-f]{6}$/i.test(color) ? { label: color, value: color } : null);

export const productColors = (product) => {
  const alternateVariants = Array.isArray(product.colorVariants) ? product.colorVariants : [];
  const variants = [
    ...(product.primaryColor && product.image
      ? [{ color: product.primaryColor, name: product.primaryColorName || colorSwatch(product.primaryColor)?.label, image: product.image }]
      : []),
    ...alternateVariants,
  ];
  return variants
    .filter((variant) => variant.color && colorSwatch(variant.color))
    .filter((variant, index, all) => all.findIndex((item) => item.color === variant.color) === index);
};

export const isApparel = (product) => APPAREL_CATEGORIES.includes(product.category);

export const productSizes = (product) => [...new Set((product.sizes ?? []).map((size) => String(size).trim()).filter(Boolean))];

function ProductCard({ product }) {
  const { name, price, oldPrice, tag, image } = product;
  const [selectedColor, setSelectedColor] = useState('');
  const colors = productColors(product);
  const activeVariant = colors.find((variant) => variant.color === selectedColor);
  const shownImage = activeVariant?.image || image;
  const colorSignature = colors.map((variant) => variant.color).join(',');

  useEffect(() => {
    setSelectedColor('');
  }, [product.id, colorSignature]);

  const to = `/product/${product.id}${selectedColor ? `?color=${selectedColor}` : ''}`;

  return (
    <article className={`product-card${product.outOfStock ? ' product-card--out' : ''}`}>
      <Link className="product-card__media" to={to} tabIndex={-1} aria-hidden="true">
        {shownImage ? (
          <img className="product-card__img" src={shownImage} alt="" loading="lazy" />
        ) : (
          <span className="product-card__photo-note">Photo coming soon</span>
        )}
        {tag && (
          <span className={`product-card__tag product-card__tag--${tag}`}>{TAG_LABELS[tag] || tag}</span>
        )}
      </Link>

      {colors.length > 0 && (
        <div className="product-card__colors" role="group" aria-label="Available colors">
          {colors.map((variant) => {
            const swatch = colorSwatch(variant.color);
            const label = variant.name || swatch.label;
            return (
              <button
                type="button"
                key={variant.color}
                className={`product-card__swatch${selectedColor === variant.color ? ' is-selected' : ''}`}
                style={{ backgroundColor: swatch.value }}
                title={label}
                aria-label={label}
                aria-pressed={selectedColor === variant.color}
                onClick={() => setSelectedColor((current) => (current === variant.color ? '' : variant.color))}
              />
            );
          })}
        </div>
      )}

      <div className="product-card__body">
        <h3 className="product-card__name">
          <Link className="product-card__link" to={to}>{name}</Link>
        </h3>

        <p className="product-card__price">
          <span className="product-card__price-now">{formatPrice(price)}</span>
          {oldPrice && <span className="product-card__price-was">{formatPrice(oldPrice)}</span>}
          {product.outOfStock && <span className="product-card__sold">Sold out</span>}
        </p>
      </div>
    </article>
  );
}

export default ProductCard;
